var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { computed, defineComponent } from 'vue';
import { TinyColor } from '@ctrl/tinycolor';
import initDefaultProps from '../_util/props-util/initDefaultProps';
import CloseOutlined from '@ant-design/icons-vue/CloseOutlined';
import CheckOutlined from '@ant-design/icons-vue/CheckOutlined';
import CheckCircleFilled from '@ant-design/icons-vue/CheckCircleFilled';
import CloseCircleFilled from '@ant-design/icons-vue/CloseCircleFilled';
import classNames from '../_util/classNames';
import Line from './Line';
import Circle from './Circle';
import Steps from './Steps';
import { getSize, getSuccessPercent, validProgress } from './utils';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import { useConfigContextInject } from '../config-provider/context';
import devWarning from '../vc-util/devWarning';
import { progressProps, progressStatuses } from './props';
import useStyle from './style';
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'AProgress',
    inheritAttrs: false,
    props: initDefaultProps(progressProps(), {
        type: 'line',
        percent: 0,
        showInfo: true,
        // null for different theme definition
        trailColor: null,
        size: 'default',
        strokeLinecap: 'round',
    }),
    slots: Object,
    setup(props, { slots, attrs }) {
        const { prefixCls, direction } = useConfigInject('progress', props);
        const { progress: ctxProgress } = useConfigContextInject();
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const mergedTrailColor = computed(() => { var _a; return (_a = props.railColor) !== null && _a !== void 0 ? _a : props.trailColor; });
        const mergedGapPosition = computed(() => { var _a; return (_a = props.gapPlacement) !== null && _a !== void 0 ? _a : props.gapPosition; });
        if (process.env.NODE_ENV !== 'production') {
            devWarning('successPercent' in props, 'Progress', '`successPercent` is deprecated. Please use `success.percent` instead.');
            devWarning('width' in props, 'Progress', '`width` is deprecated. Please use `size` instead.');
        }
        const strokeColorNotArray = computed(() => Array.isArray(props.strokeColor) ? props.strokeColor[0] : props.strokeColor);
        const percentNumber = computed(() => {
            const { percent = 0 } = props;
            const successPercent = getSuccessPercent(props);
            return parseInt(successPercent !== undefined ? successPercent.toString() : percent.toString(), 10);
        });
        const progressStatus = computed(() => {
            const { status } = props;
            if (!progressStatuses.includes(status) && percentNumber.value >= 100) {
                return 'success';
            }
            return status || 'normal';
        });
        const mergedSteps = computed(() => {
            const { steps } = props;
            if (steps === undefined || steps === null) {
                return null;
            }
            if (typeof steps === 'object') {
                return { count: steps.count, gap: steps.gap };
            }
            return { count: steps, gap: undefined };
        });
        const infoAlign = computed(() => { var _a, _b; return (_b = (_a = props.percentPosition) === null || _a === void 0 ? void 0 : _a.align) !== null && _b !== void 0 ? _b : 'end'; });
        const infoPosition = computed(() => { var _a, _b; return (_b = (_a = props.percentPosition) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : 'outer'; });
        const strokeColorIsBright = computed(() => {
            const colorValue = strokeColorNotArray.value;
            if (!colorValue) {
                return false;
            }
            const color = typeof colorValue === 'string' ? colorValue : Object.values(colorValue)[0];
            return typeof color === 'string' ? new TinyColor(color).isLight() : false;
        });
        const classString = computed(() => {
            const { type, showInfo, size } = props;
            const pre = prefixCls.value;
            const isLineType = type === 'line';
            const isPureLineType = isLineType && !mergedSteps.value;
            return {
                [pre]: true,
                [`${pre}-inline-circle`]: type === 'circle' && getSize(size, 'circle').width <= 20,
                [`${pre}-${(type === 'dashboard' && 'circle') || type}`]: type !== 'line',
                [`${pre}-line`]: isPureLineType,
                [`${pre}-line-align-${infoAlign.value}`]: isPureLineType,
                [`${pre}-line-position-${infoPosition.value}`]: isPureLineType,
                [`${pre}-status-${progressStatus.value}`]: true,
                [`${pre}-show-info`]: showInfo,
                [`${pre}-${size}`]: typeof size === 'string',
                [`${pre}-steps`]: !!mergedSteps.value,
                [`${pre}-rtl`]: direction.value === 'rtl',
                [hashId.value]: true,
            };
        });
        const strokeColorNotGradient = computed(() => typeof props.strokeColor === 'string' || Array.isArray(props.strokeColor)
            ? props.strokeColor
            : undefined);
        const renderProcessInfo = () => {
            const { showInfo, format, type, percent, title } = props;
            const successPercent = getSuccessPercent(props);
            if (!showInfo)
                return null;
            let text;
            const textFormatter = format || (slots === null || slots === void 0 ? void 0 : slots.format) || ((val) => `${val}%`);
            const isLineType = type === 'line';
            const isPureLineType = isLineType && !mergedSteps.value;
            const isBrightInnerColor = isLineType && strokeColorIsBright.value && infoPosition.value === 'inner';
            if (infoPosition.value === 'inner' ||
                format ||
                (slots === null || slots === void 0 ? void 0 : slots.format) ||
                (progressStatus.value !== 'exception' && progressStatus.value !== 'success')) {
                text = textFormatter(validProgress(percent), validProgress(successPercent));
            }
            else if (progressStatus.value === 'exception') {
                text = isLineType ? <CloseCircleFilled /> : <CloseOutlined />;
            }
            else if (progressStatus.value === 'success') {
                text = isLineType ? <CheckCircleFilled /> : <CheckOutlined />;
            }
            return (<span class={classNames(`${prefixCls.value}-text`, {
                    [`${prefixCls.value}-text-bright`]: isBrightInnerColor,
                    [`${prefixCls.value}-text-${infoAlign.value}`]: isPureLineType,
                    [`${prefixCls.value}-text-${infoPosition.value}`]: isPureLineType,
                })} title={title === undefined && typeof text === 'string' ? text : undefined}>
          {text}
        </span>);
        };
        return () => {
            const { type, title } = props;
            const { class: cls, style: attrStyle } = attrs, restAttrs = __rest(attrs, ["class", "style"]);
            const progressCfg = ctxProgress === null || ctxProgress === void 0 ? void 0 : ctxProgress.value;
            const progressInfo = renderProcessInfo();
            const stepsInfo = mergedSteps.value;
            const trailColor = mergedTrailColor.value;
            const gapPosition = mergedGapPosition.value;
            let progress;
            // Render progress shape
            if (type === 'line') {
                progress = stepsInfo ? (<Steps {...props} strokeColor={strokeColorNotGradient.value} prefixCls={prefixCls.value} steps={stepsInfo.count} stepGap={stepsInfo.gap} trailColor={trailColor}>
            {progressInfo}
          </Steps>) : (<Line {...props} strokeColor={strokeColorNotArray.value} prefixCls={prefixCls.value} direction={direction.value} percentPosition={{
                        align: infoAlign.value,
                        type: infoPosition.value,
                    }} trailColor={trailColor}>
            {progressInfo}
          </Line>);
            }
            else if (type === 'circle' || type === 'dashboard') {
                progress = (<Circle {...props} prefixCls={prefixCls.value} strokeColor={strokeColorNotArray.value} progressStatus={progressStatus.value} trailColor={trailColor} gapPosition={gapPosition}>
            {progressInfo}
          </Circle>);
            }
            return wrapSSR(<div role="progressbar" {...restAttrs} class={[classString.value, progressCfg === null || progressCfg === void 0 ? void 0 : progressCfg.className, cls]} style={[progressCfg === null || progressCfg === void 0 ? void 0 : progressCfg.style, attrStyle]} title={title}>
          {progress}
        </div>);
        };
    },
});
